import type { ReactNode } from "react";
import { cn } from "../primitives/utils";

export interface StackedField {
  label: string;
  value: ReactNode;
}

export interface StackedRow {
  key: string;
  /** The row's identifier, usually the link to its record. */
  title: ReactNode;
  /** Beside the title: a row's tick box. */
  leading?: ReactNode;
  /** Every other column, label then value. */
  fields: StackedField[];
  /** The row's own commands, under its fields. */
  actions?: ReactNode;
  /** Tapping the row anywhere but a control opens its record. */
  onOpen?: () => void;
}

/**
 * A table's rows drawn as stacked cards, for a phone: the identifier on top,
 * then each field it is given as a label and its value, then the row's
 * commands. It draws every field it is given; a record's child rows leave out
 * the fields a row holds nothing in before they reach it (`isEmptyCell`).
 */
export function StackedRows({
  rows,
  ariaLabel,
  className,
}: {
  rows: StackedRow[];
  ariaLabel: string;
  className?: string;
}) {
  return (
    <ul
      aria-label={ariaLabel}
      className={cn("divide-y divide-border", className)}
    >
      {rows.map((row) => (
        <li
          key={row.key}
          className={cn("space-y-2 px-3 py-3", row.onOpen && "cursor-pointer")}
          onClick={row.onOpen}
        >
          <div className="flex min-w-0 items-start gap-3">
            {row.leading && (
              <span onClick={(e) => e.stopPropagation()} className="pt-0.5">
                {row.leading}
              </span>
            )}
            <span className="min-w-0 break-words font-medium tabular-nums">
              {row.title}
            </span>
          </div>
          {row.fields.length > 0 && (
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
              {row.fields.map((field) => (
                <div key={field.label} className="contents">
                  <dt className="text-muted-foreground">{field.label}</dt>
                  <dd className="min-w-0 break-words text-right">
                    {field.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {row.actions && (
            <div
              className="flex flex-wrap gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {row.actions}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
